import { module } from "angular";
import "angular-translate";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { duration } from "moment-timezone";

import type { IHttpService } from "App/Modules/Services";
import type { IEvent, IEventsGroup, IResponse } from "../../Models";
import {
    EventAssessmentClass,
    EventBlockType,
    EventClassification,
    EventStepType,
    EventType,
    EventTypeFunctions,
    Progress,
} from "../../Models";

import type { IEventsRepository } from "./IEventsRepository";

export default class EventsRepository implements IEventsRepository {
    public static $inject = ["mmHttpService", "$translate"];

    constructor(
        private readonly httpService: IHttpService,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public getEvents(
        siteId: string,
        fromDate: string,
        toDate: string,
        assessment: number | null,
        offset: number | null,
        count: number | null,
    ): Observable<IResponse<IEventsGroup>> {
        const params = { fromDate, toDate, assessment, offset, count };

        return this.httpService.get<IEventsGroup>(`sites/${siteId}/events`, params).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.events.forEach((e) => {
                    this.enhanceEvent(e);
                    e.typeNameLocalized = this.$translate.instant(`Enums.EventType.${e.typeName}`);
                    e.assessmentNameLocalized = this.$translate.instant(
                        `Entities.AlarmEvents.FilterAssessment${e.assessmentName}`,
                    );
                });

                return response;
            }),
        );
    }

    public getEvent(siteId: string, eventId: string): Observable<IResponse<IEvent>> {
        return this.httpService.get<IEvent>(`sites/${siteId}/events/${eventId}`).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                this.enhanceEvent(response.data);
                // eslint-disable-next-line
                this.$translate(`Enums.EventType.${response.data.typeName}`)
                    .then((translatedType) => {
                        response.data.typeNameLocalized = translatedType;
                    })
                    .catch((e) => {
                        console.log(`Error when translating event type: ${e}`);
                    });

                return response;
            }),
        );
    }

    private enhanceEvent(event: IEvent): IEvent {
        event.typeName = event.eventType as any as keyof typeof EventType;
        event.eventType = EventType[event.typeName];
        event.assessmentName = event.eventAssessmentClass as any as keyof typeof EventAssessmentClass;
        event.eventAssessmentClass = EventAssessmentClass[event.assessmentName];
        event.classificationName = event.eventClassification as any as keyof typeof EventClassification;
        event.eventClassification = EventClassification[event.classificationName];
        event.progressName = event.progress as any as keyof typeof Progress;
        event.progress = Progress[event.progressName];
        event.icon = EventTypeFunctions.eventTypeIcon(event.eventType);

        if (event.eventBlocks) {
            event.eventBlocks.forEach((eb) => {
                eb.eventBlockTypeName = eb.eventBlockType as any as keyof typeof EventBlockType;
                eb.eventBlockType = EventBlockType[eb.eventBlockTypeName];
                eb.duration = eb.dtFinished && eb.dtStarted ? duration(eb.dtFinished.diff(eb.dtStarted)) : null;
                eb.eventSteps.forEach((es) => {
                    es.eventStepTypeName = es.eventStepType as any as keyof typeof EventStepType;
                    es.eventStepType = EventStepType[es.eventStepTypeName];
                });
            });
        }

        return event;
    }
}

module("mmRepositories").service("mmEventsRepository", EventsRepository);
